import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import WTypography from '../../../Components/whiteTypography'
import ConseillerChat from '../../ConseillerPatrimonial/components/ConseillerChat'
import { loadWallet } from '../../../Components/Billing/billingThunks'
import { makePostRequest } from 'utils/api'

import { buildEcoAdvisor } from '../utils/buildEcoAdvisor'
import { fetchEcoAdvisorSources } from '../utils/fetchEcoAdvisorSources'
import {
  EcoScoreCard,
  EcoDiagnosticPanel,
  EcoActionsPanel,
  EcoBreakdownPanel,
  EcoRecommendationPanel,
} from './EcoAdvisorSections'

/**
 * LAP-766 — Page principale "Mon conseiller écologie IA".
 *
 * Pattern : strict miroir de `ConseillerTempsPage` / `ConseillerPatrimonialPage`.
 *   - On agrège l'empreinte carbone (logement/transport/alimentation/shopping)
 *     + la dernière recommandation Claude depuis /api/Dashboard/reports/.
 *   - On passe l'agrégat à `buildEcoAdvisor` qui produit score / diagnostic /
 *     actions / répartition / recommandations.
 *   - On expose un chat (réutilisation directe de `ConseillerChat`) pour les
 *     questions libres.
 *
 * Route : /conseiller-ecologie (cf. App.js).
 */
function ConseillerEcologiePage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation('layout')

  const [chatOpen, setChatOpen] = useState(true)
  const [snackbar, setSnackbar] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())

  const [rawSources, setRawSources] = useState(null)
  const siteUser = useSelector((s) => s.user)

  // Recalcul de l'advisor à chaque changement de sources OU de langue.
  const advisorData = useMemo(() => {
    if (!rawSources) return null
    return buildEcoAdvisor({ ...rawSources, t })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSources, t, i18n.language])

  const displayedFirstName =
    advisorData?.userProfile?.firstName ||
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    fetchAllSources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * LAP-766 — Agrégation des sources Life du conseiller écologie via
   * `fetchEcoAdvisorSources` (endpoints isolés, jamais de throw). Si une source
   * manque, l'advisor produit un diagnostic partiel mais exploitable.
   */
  const fetchAllSources = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) setRegenerating(true)
    else setInitialLoading(true)

    try {
      const sources = await fetchEcoAdvisorSources(siteUser?.user || {})
      setRawSources(sources)
      setLastUpdate(new Date())

      if (forceRefresh) {
        try {
          await dispatch(loadWallet())
        } catch (_) {
          /* silencieux */
        }
        setSnackbar({
          severity: 'success',
          message: t('conseillerEcologie.regenerateSuccess', {
            defaultValue: 'Analyse écologique actualisée.',
          }),
        })
      }
    } catch (e) {
      console.warn('[ConseillerEcologie] fetch error', e?.message)
      if (!rawSources) {
        setRawSources({ user: siteUser?.user || {}, globalEmission: [], advice: {} })
      }
      if (forceRefresh) {
        setSnackbar({
          severity: 'error',
          message: t('conseillerEcologie.regenerateError', {
            defaultValue: 'Impossible d’actualiser l’analyse pour le moment.',
          }),
        })
      }
    } finally {
      if (forceRefresh) setRegenerating(false)
      else setInitialLoading(false)
    }
  }

  const handleRegenerate = () => {
    if (regenerating || initialLoading) return
    fetchAllSources({ forceRefresh: true })
  }

  /**
   * LAP-766 — Envoi d'un message au conseiller écologie.
   * CONTRAT BACKEND attendu :
   *   POST /api/Dashboard/eco-advisor/chat/
   *     body: { message, locale, feature: 'eco_chat', context }
   *     output: { reply, source, locale, generated_at }
   * Tant que l'endpoint n'existe pas, on relaie le rejet à ConseillerChat qui
   * utilise sa réponse de démo.
   */
  const handleChatSend = async (message, locale) => {
    try {
      const response = await makePostRequest(
        '/api/Dashboard/eco-advisor/chat/',
        {
          message,
          locale,
          feature: 'eco_chat',
          context: advisorData
            ? {
                totals: advisorData.totals,
                score: advisorData.ecoScore.global,
                topRisks: advisorData.diagnostic.risks.map((r) => r.key),
              }
            : null,
        },
        {},
        true,
      )
      const data = response?.data || {}
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || data.text || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      console.warn('[ConseillerEcologie] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #00120c 0%, #0a241a 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
          px: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#2fd28a' }} />
        <Box>
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerEcologie.initialLoading', {
              defaultValue: 'Votre conseiller analyse votre empreinte carbone…',
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#8fe3c0 !important', mt: 1, maxWidth: 480 }}>
            {t('conseillerEcologie.initialLoadingHint', {
              defaultValue:
                'On agrège votre logement, vos transports, votre alimentation et votre consommation, comparés à la moyenne nationale.',
            })}
          </WTypography>
        </Box>
      </Box>
    )
  }

  const initialChatMessages = [
    {
      role: 'agent',
      content: t('conseillerEcologie.chat.welcome', {
        defaultValue: `Bonjour ${displayedFirstName || ''} 🌱 Je suis votre conseiller écologie. Posez-moi n’importe quelle question sur votre empreinte carbone et comment la réduire.`,
        name: displayedFirstName,
      }),
      source: t('dashboard.lifeAgentSignature'),
      timestamp: new Date().toISOString(),
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #00120c 0%, #0a241a 100%)',
        color: 'white',
        pb: 6,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(0,18,12,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/Dashboard')}
            sx={{ color: '#cdeede', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('conseillerEcologie.pageTitle', {
                defaultValue: 'Mon conseiller écologie',
              })}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{ color: '#8fe3c0 !important', display: { xs: 'none', md: 'block' } }}
            >
              {t('conseillerEcologie.lastUpdated', {
                defaultValue: `Mis à jour ${formatRelativeI18n(lastUpdate, t)}`,
                date: formatRelativeI18n(lastUpdate, t),
              })}
            </WTypography>
          </Box>

          <IconButton
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              color: '#cdeede',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              '&.Mui-disabled': { color: 'rgba(205,238,222,0.4)' },
            }}
            aria-label={t('conseillerEcologie.regenerate', {
              defaultValue: 'Actualiser l’analyse',
            })}
            title={t('conseillerEcologie.regenerate', {
              defaultValue: 'Actualiser l’analyse',
            })}
          >
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexShrink: 0,
              bgcolor: chatOpen ? '#2fd28a' : 'transparent',
              color: chatOpen ? '#00120c' : '#cdeede',
              border: chatOpen ? 'none' : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: chatOpen ? '#27b576' : 'rgba(255,255,255,0.06)',
              },
            }}
            aria-label={
              chatOpen
                ? t('conseillerEcologie.hideChat', { defaultValue: 'Masquer le chat' })
                : t('conseillerEcologie.openChat', { defaultValue: 'Ouvrir le chat' })
            }
          >
            <ChatBubbleOutlineIcon />
          </IconButton>

          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setChatOpen(!chatOpen)}
            variant={chatOpen ? 'contained' : 'outlined'}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              ...(chatOpen
                ? { bgcolor: '#2fd28a', color: '#00120c', '&:hover': { bgcolor: '#27b576' } }
                : { color: '#cdeede', borderColor: 'rgba(255,255,255,0.2)' }),
            }}
          >
            {chatOpen
              ? t('conseillerEcologie.hideChat', { defaultValue: 'Masquer le chat' })
              : t('conseillerEcologie.openChat', { defaultValue: 'Ouvrir le chat' })}
          </Button>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            lg: chatOpen ? '2fr 1fr' : '1fr',
          }}
          gap={3}
          alignItems="flex-start"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {advisorData && (
              <>
                <EcoScoreCard
                  score={advisorData.ecoScore}
                  totals={advisorData.totals}
                  userFirstName={displayedFirstName}
                />
                <EcoRecommendationPanel recommendation={advisorData.recommendation} />
                <EcoDiagnosticPanel
                  diagnostic={advisorData.diagnostic}
                  recommendations={advisorData.recommendations}
                />
                <EcoBreakdownPanel breakdown={advisorData.breakdown} />
                <EcoActionsPanel actions={advisorData.actionsPrioritaires} />
              </>
            )}
          </Box>

          {chatOpen && (
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 90 },
                alignSelf: 'flex-start',
                minWidth: 0,
              }}
            >
              <ConseillerChat
                messages={initialChatMessages}
                suggestedPrompts={advisorData?.suggestedPrompts || []}
                userLocale={i18n.language || 'fr-FR'}
                agentSignature={t('dashboard.lifeAgentSignature')}
                onSend={handleChatSend}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

function formatRelativeI18n(date, t) {
  if (!date) return ''
  const now = Date.now()
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const diff = Math.max(0, now - then)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1)
    return t('conseillerEcologie.relativeTime.justNow', { defaultValue: 'à l’instant' })
  if (minutes < 60)
    return t('conseillerEcologie.relativeTime.minutesAgo', {
      defaultValue: `il y a ${minutes} min`,
      n: minutes,
    })
  if (hours < 24)
    return t('conseillerEcologie.relativeTime.hoursAgo', {
      defaultValue: `il y a ${hours} h`,
      n: hours,
    })
  return t('conseillerEcologie.relativeTime.daysAgo', {
    defaultValue: `il y a ${days} j`,
    n: days,
  })
}

export default ConseillerEcologiePage
